import React, { useEffect, useRef, useState, ChangeEvent } from "react"
import { useMutation } from "@tanstack/react-query"; 

import { supabase } from "../supabaseClient";
import { loadMapKit } from "../MapKitLoader";

import { JamPadArtist } from "../DataModels";
import { Tag } from "../Tags.ts";
import { UUID } from "crypto";
import { v4 as uuid } from 'uuid';

import { TextField, Box, Button, Autocomplete, Grid, Typography, Switch, FormControlLabel, InputAdornment, MenuItem, Divider } from "@mui/material";
import { CheckCircle, Clear, MusicNote } from "@mui/icons-material";

import { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import EventArtistSearch from "./EventArtistSearch.tsx";
import EventTagAdder from "./EventTagAdder.tsx";

export const ages = [
    { label: "All Ages", val: 0}, 
    { label: "16", val: 16},
    { label: "18", val: 18},
    { label: "21", val: 21},
]

interface PostInput {
    id: UUID;
    title: string | null;
    description: string | null;
    price: number;
    age: number;
    private_event: boolean;

    poster: string | null;
    artist_names: string[];
    artist_ids: Array<string | null>;
    tags: string[];

    date: Date;
    end_date: Date | null;
    venue_id: UUID;
    verified: boolean;
};


const createPost = async (post: PostInput, place: any, img: File | null) => {
    const address_in = place?.formattedAddress;
    const name_in = place?.name;
    const lat_in = place?.coordinate.latitude;
    const lng_in = place?.coordinate.longitude;
    if(!address_in || !name_in || !lat_in || !lng_in) {
        alert("Malformed map location.")
        return null;
    }
    const { data: venueID, error: venueErr } = await supabase
        .rpc("venue_id_from_address", { address_in, name_in, lat_in, lng_in })
        .select().single();
    if(venueErr) {
        console.error("Failed to load venue: " + venueErr.message);
        alert("Failed to create event. Could not parse venue ID.");
        return null;
    } else {
        post.venue_id = venueID;
    }

    if (img) {
        const filePath = `${post.id}.jpeg`
        const {error: uploadError} = await supabase.storage.from("posters").upload(filePath, img)
        if (uploadError) {
            console.error("Failed to upload poster: " + uploadError.message);
            alert("Faield to create event. Could not upload poster");
            return null;
        }
        const {data: image} = supabase.storage.from("posters").getPublicUrl(filePath);
        post.poster = image.publicUrl;
    }

    const { error } = await supabase.from("events").insert({ ...post });
    if (error) {
        console.error("Event creation failed: " + error.message);
        alert("Failed to create event.");
        return null;
    }
    alert("Success!");
    return post.id;
}



export const CreateEvent = () => {
    const imgUpload = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | undefined>();

    const [title, setTitle] = useState("");
    const [date, setDate] = useState<Dayjs | null>(null);
    const [hasEndDate, toggleEndDate] = useState(false);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);

    const [search, setSearch] = useState<any | null>(null);
    const [selectedAddress, selectAddress] = useState<any | null>(null);
    const [addressIn, setAddressIn] = useState("");
    const [address, setAddress] = useState("");
    const [matchingAddresses, setAddresses] = useState<any[]>([]);

    const [privateEvent, setPrivate] = useState(false);
    const [artists, setArtists] = useState<JamPadArtist[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [price, setPrice] = useState(0);
    const [age, setAge] = useState(0);
    const [description, setDesc] = useState("");

    // converts form data to interface data
    const {mutate} = useMutation({
        mutationFn: (data: {post: PostInput, place: any, img: File | null }) => {
            return createPost(data.post, data.place, data.img);
        }, 
    });

    const clearPost = () => {
        const doClear = window.confirm("Clear all values from your current form?");
        if(doClear) {
            setSelectedFile(null);
            setPreview(undefined);
            setTitle("");
            setDate(null);
            toggleEndDate(false);
            setEndDate(null);
            setAddress("");
            selectAddress(null);
            setAddresses([]);
            setPrivate(false);
            setArtists([]);
            setTags([]);
            setPrice(0);
            setAge(0);
            setDesc("");
        }
    }

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault()
        if (!selectedAddress?.formattedAddress) {
            alert("Please enter a valid address.")
            return;
        } else if (date === null) {
            alert("Please enter a valid start date.")
            return;
        } else if (hasEndDate && endDate === null) {
            alert("Please enter a valid end date.")
            return;
        }
        mutate({ post: { 
            id: uuid() as UUID, poster: "", venue_id: uuid() as UUID, verified: false,
            title, description, price, age, date: date.toDate(), end_date: endDate?.toDate() ?? null,
            artist_names: artists.map((a) => { return a.name; }), 
            artist_ids: artists.map((a) => { return a.id; }), 
            tags, private_event: privateEvent
        }, place: selectedAddress, img: selectedFile });
    };
 
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setPreview(URL.createObjectURL(e.target.files[0]));
            setSelectedFile(e.target.files[0]);
        }
    };

    // handle address search
    useEffect(() => {
        if(search === null) {
            loadMapKit().then((mapkit) => {
                const s = new mapkit.Search({ language: 'en-US', getsUserLocation: true });
                setSearch(s);
                searchEventAddresses();
            });
        } else {
            searchEventAddresses();
        }
    }, [addressIn, search]);
    function searchEventAddresses() {
        if(search === null || addressIn === '' || addressIn === address) { return }
        search.search(addressIn, function(error, data) {
            if (error) {
                console.error("Error during Apple Maps search: " + error.message)
                return;
            }
            setAddresses(data.places);
        });
    }

    return <Box component="form" sx={{ 
            p: '30px 10vw', pb: '140px', display: 'flex', flexDirection: 'column',
            justifyContent: 'center', alignItems: 'center', gap: '18px',
        }} noValidate autoComplete="off">
            <div style={{ width: '100%', display: 'flex' }}>
                <Typography  variant='h3' sx={{ fontWeight: 'bold', flex: '1 0 50%' }}>Event Creator</Typography>
                <div style={{ flex: '1' }}></div>
                <Button sx={{ color: 'red', p: '0 10px', m: 0, fontSize: 14, gap: '4px', borderRadius: 4 }} 
                    variant="text" component="button" onClick={clearPost}
                >
                    <Clear fontSize="small"/>Reset
                </Button>
            </div>
            <Button style={{ background: 'darkblue', alignSelf: 'start' }} variant="contained" 
                component="button" onClick={() => imgUpload.current?.click()}
            >
                Upload Event Poster
                <input style={{ display: 'none' }} accept="image/*" id="contained-button-file"
                    type="file" onChange={handleFileChange} ref={imgUpload}/>
            </Button>
            {preview && <img style={{ maxHeight: '70vh', width: '100%', objectFit: 'contain' }}
                src={preview} alt="Uploaded poster preview" />}

            <TextField label="Event Title" placeholder="Title (optional)" fullWidth
                value={title} onChange={(e) => { setTitle(e.target.value) }} />

            <Autocomplete fullWidth freeSolo filterOptions={(x) => x}
                value={address} noOptionsText="No matching locations"
                options={matchingAddresses} getOptionLabel={(option) => option.formattedAddress ?? "" }
                onChange={(_, selected) => { 
                    selectAddress(selected)
                    setAddressIn('')
                 }}
                onInputChange={(_, val) => { setAddressIn(val) }} // update search text
                renderInput={(params) => (
                    <div style={{ flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'left', gap: '6px' }}>
                        <div style={{ flex: '1', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            { selectedAddress !== null &&
                                <CheckCircle style={{ color: "green", width: '30px' }}/>
                            }
                            <TextField {...params} label="Event Address" fullWidth required />
                        </div>
                        { selectedAddress && 
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'gray', pl: '50px' }}>
                                { selectedAddress.name }
                            </Typography>
                        }
                    </div>
                )}
                renderOption={({ key, ...props }, place) => (
                    <li key={place.muid} {...props}>
                        <Grid sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                            <Box component="span">{place.name}</Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {place.formattedAddress}
                            </Typography>
                        </Grid>
                    </li>
                )}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <DateTimePicker sx={{ flex: '1'}} label="Start Date/Time"
                        value={date} onChange={(d) => setDate(d)}
                    />
                </div>
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <FormControlLabel control={
                        <Switch checked={hasEndDate}
                            onChange={(e) => toggleEndDate(e.target.checked)}
                            slotProps={{ input: { 'aria-label': 'controlled' } }}
                        />
                    } label="Set End Date" />
                    { hasEndDate && 
                        <DateTimePicker sx={{ flex: '1'}} label="End Date/Time"
                            value={endDate} onChange={(d) => setEndDate(d)}
                        />
                    }
                </div>
            </LocalizationProvider>

            <Divider orientation="horizontal" flexItem 
                style={{ margin: '14px 0',}} 
            />

            <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                <TextField required style={{ flex: '1' }} label="Price" type="number"
                    value={price} onChange={(e) => setPrice(parseInt(e.target.value))}
                    slotProps={{ input: { 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}}/>
                    
                <TextField style={{ flex: '1' }} required select label="Ages" 
                    value={age} onChange={(e) => setAge(parseInt(e.target.value))}>
                    {ages.map((option) => (
                        <MenuItem key={option.val} value={option.val}>
                            {option.label}{option.val > 0 ? "+" : ""}
                        </MenuItem>
                    ))}
                </TextField>
                <FormControlLabel control={
                    <Switch checked={privateEvent}
                        onChange={(e) => setPrivate(e.target.checked)}
                        slotProps={{ input: { 'aria-label': 'controlled' } }}
                    />
                } label="Private Event" />

                <EventArtistSearch artists={artists} 
                    addArtist={(a) => {
                        if(artists.find((artist) => {
                            return a.name === artist.name && a.id === artist.id;
                        }) === undefined){
                            const temp = Array.from(artists.values());
                            temp.push(a);
                            setArtists(temp);
                        }
                    }} 
                    removeArtist={(a) => {
                        const temp = Array.from(artists.filter((artist) => {
                            return !(artist.name === a.name && artist.id === a.id)
                        }).values());
                        setArtists(temp);
                    }}
                />
            </div>
            
            <Divider orientation="horizontal" flexItem 
                style={{ margin: '14px 0',}} 
            />

            <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                <EventTagAdder tags={tags} 
                    removeTag={(t) => {
                        const temp = Array.from(tags.filter((tag) => {
                            return tag !== t;
                        }).values());
                        setTags(temp);
                    }}
                    addTag={(t) => {
                        const temp = Array.from(tags.values());
                        temp.push(t);
                        setTags(temp);
                    }}
                />
                <TextField label="Description" fullWidth
                    value={description} onChange={(e) => setDesc(e.target.value)}
                    multiline rows={2}
                    placeholder="Any additional details for this event..."
                />
                <Button variant="contained" sx={{ 
                    background: 'darkblue', padding: '16px 14px', borderRadius: '16px',
                    display: 'flex', alignItems: 'center', gap: '10px', margin: '0 auto'
                }} onClick={(e) => {handleSubmit(e)}}>
                    <MusicNote/>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>Create Event</div>
                </Button>
            </div>
        </Box>;
};
export default CreateEvent